/*
 * MarineMaker · <mm-project-gallery>
 * Category filters (aria-pressed + live count) and a native <dialog> lightbox.
 * Logic ported from handoff/design-reference/OurWork.dc.html (renderVals()).
 * No dependencies. Loaded with defer from sections/mm-project-gallery.liquid.
 */
(() => {
  if (customElements.get('mm-project-gallery')) return;

  class MMProjectGallery extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onDialogKeydown = this.onDialogKeydown.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.filter = 'all';
      this.items = Array.from(this.querySelectorAll('[data-mm-item]'));
      this.filterButtons = Array.from(this.querySelectorAll('[data-mm-filter]'));
      this.countEl = this.querySelector('[data-mm-count]');
      this.dialog = this.querySelector('[data-mm-lightbox]');
      this.index = -1;
      this.opener = null;

      this.addEventListener('click', this.onClick);
      if (this.dialog) {
        this.lb = {
          pos: this.dialog.querySelector('[data-mm-pos]'),
          media: this.dialog.querySelector('[data-mm-media]'),
          img: this.dialog.querySelector('[data-mm-img]'),
          title: this.dialog.querySelector('[data-mm-title]'),
          caption: this.dialog.querySelector('[data-mm-caption]'),
          prev: this.dialog.querySelector('[data-mm-prev]'),
          next: this.dialog.querySelector('[data-mm-next]')
        };
        this.dialog.addEventListener('keydown', this.onDialogKeydown);
        this.dialog.addEventListener('click', this.onDialogClick);
        this.dialog.addEventListener('close', this.onDialogClose);
      }
      // Theme editor: show a selected block even if a filter currently hides it.
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.applyFilter('all', false);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.dialog) {
        this.dialog.removeEventListener('keydown', this.onDialogKeydown);
        this.dialog.removeEventListener('click', this.onDialogClick);
        this.dialog.removeEventListener('close', this.onDialogClose);
        if (this.dialog.open) this.dialog.close();
      }
    }

    visibleItems() {
      return this.items.filter((item) => !item.hidden);
    }

    applyFilter(filter, announce = true) {
      this.filter = filter;
      this.dataset.filter = filter;
      this.filterButtons.forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.mmFilter === filter));
      });
      this.items.forEach((item) => {
        item.hidden = filter !== 'all' && item.dataset.category !== filter;
      });
      if (this.countEl && announce) {
        const n = this.visibleItems().length;
        this.countEl.textContent = 'Showing ' + n + (n === 1 ? ' photo' : ' photos');
      }
    }

    onClick(event) {
      const filterBtn = event.target.closest('[data-mm-filter]');
      if (filterBtn && this.contains(filterBtn)) {
        if (filterBtn.dataset.mmFilter !== this.filter) this.applyFilter(filterBtn.dataset.mmFilter);
        return;
      }
      const tile = event.target.closest('[data-mm-open]');
      if (tile && this.contains(tile)) {
        const item = tile.closest('[data-mm-item]');
        const index = this.visibleItems().indexOf(item);
        if (index > -1) this.open(index, tile);
      }
    }

    open(index, opener) {
      if (!this.dialog) return;
      this.opener = opener || null;
      this.show(index);
      if (!this.dialog.open) {
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }
      }
      const close = this.dialog.querySelector('[data-mm-close]');
      if (close) close.focus();
    }

    show(index) {
      const list = this.visibleItems();
      const n = list.length;
      if (!n) return;
      this.index = ((index % n) + n) % n;
      const tile = list[this.index].querySelector('[data-mm-open]');
      const d = tile.dataset;
      const { img, media, pos, title, caption, prev, next } = this.lb;

      pos.textContent = (this.index + 1) + ' / ' + n;
      title.textContent = d.title || '';
      caption.textContent = d.caption || '';
      caption.hidden = !d.caption;
      prev.hidden = n < 2;
      next.hidden = n < 2;

      // Remove any placeholder copied in for a block without an image.
      media.querySelectorAll('.mm-gallery__lb-placeholder').forEach((el) => el.remove());

      if (d.lbSrc) {
        img.hidden = false;
        img.removeAttribute('srcset');
        if (d.lbWidth) img.width = Number(d.lbWidth);
        if (d.lbHeight) img.height = Number(d.lbHeight);
        img.alt = d.alt || '';
        img.sizes = '100vw';
        if (d.lbSrcset) img.srcset = d.lbSrcset;
        img.src = d.lbSrc;
      } else {
        img.hidden = true;
        img.removeAttribute('src');
        img.removeAttribute('srcset');
        const svg = tile.querySelector('.mm-gallery__placeholder');
        if (svg) {
          const copy = svg.cloneNode(true);
          copy.setAttribute('class', 'mm-gallery__lb-placeholder');
          copy.setAttribute('aria-hidden', 'true');
          media.appendChild(copy);
        }
      }
    }

    step(delta) {
      if (this.index < 0) return;
      this.show(this.index + delta);
    }

    close() {
      if (!this.dialog || !this.dialog.open) return;
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.onDialogClose();
      }
    }

    onDialogKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'Escape' && typeof this.dialog.showModal !== 'function') {
        // Native <dialog> handles Esc itself; this covers the no-showModal fallback.
        this.close();
      }
    }

    onDialogClick(event) {
      if (event.target.closest('[data-mm-close]')) return this.close();
      if (event.target.closest('[data-mm-prev]')) return this.step(-1);
      if (event.target.closest('[data-mm-next]')) return this.step(1);
      // Click on the dialog's own padding (outside the content) closes it.
      if (event.target === this.dialog) this.close();
    }

    onDialogClose() {
      this.index = -1;
      const opener = this.opener;
      this.opener = null;
      if (opener && opener.isConnected && !opener.closest('[hidden]')) opener.focus();
    }

    onBlockSelect(event) {
      const item = event.target && event.target.closest ? event.target.closest('[data-mm-item]') : null;
      if (!item || !this.contains(item)) return;
      this.close();
      if (item.hidden) this.applyFilter('all');
      item.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }

  customElements.define('mm-project-gallery', MMProjectGallery);
})();
